/**
 * Floor planner — the selected room's editor, opened from its toolbar
 * (RoomToolbar) or the pencil by its name: the create wizard's steps as tabs
 * over one card, on the room that exists.
 *
 *   Name, Space, Size, Ceiling — live: each change commits (one undo step per
 *     field), and Done closes;
 *   Finish — a draft read from the room's product (finishOfRoom), applied
 *     with Apply, since it replaces what is on the walls and ceiling.
 *
 * Several rooms selected: no Name, Size is the wall height of them all, and
 * the ceiling and finish go to each.
 */

import React, { useMemo, useState } from 'react';
import { Check, X } from 'lucide-react';
import { DimensionInput } from '../../ui/DimensionInput';
import { CeilingEditor } from './CeilingEditor';
import { FinishStep, startingFinish } from './FinishStep';
import { SizePicker, RoomSize, presetOf, sameSize } from './SizePicker';
import { ROOM_STEP_ICON, RoomStep, sizeTitle } from './roomSteps';
import { SpacePicker } from '../chrome/SpaceMenu';
import {
  FloorPlan, PlanResult, PlanRoom, ROOM_LIMITS, StarterFinish,
  applyStarterFinish, copyCeiling, finishOfRoom, rectOfRoom, renameRoom, setCeilingProfile, setRoomRect, setRoomsHeight,
} from '../../../calculations/plan';
import { DESIGNER_STRINGS as S } from '../strings';
import './createWizard.css';
import './roomTools.css';

const W = S.planner.wizard;
const E = S.planner.edit;

/** The tabs these rooms have: Name for one room only, Space when the host takes it, Finish when there is product to put on. */
export function roomSteps(count: number, spaceTabs: boolean): RoomStep[] {
  const steps: RoomStep[] = [];
  if (count === 1) steps.push('name');
  if (spaceTabs) steps.push('space');
  steps.push('size', 'ceiling');
  if (startingFinish()) steps.push('finish');
  return steps;
}

export interface RoomEditDialogProps {
  plan: FloorPlan;
  /** The selected rooms (at least one). */
  rooms: PlanRoom[];
  step: RoomStep;
  onStep: (step: RoomStep) => void;
  onClose: () => void;
  /** Commit a result (the planner's `apply`): refusals are its business. */
  apply: (result: PlanResult, coalesceKey?: string) => boolean;
  spaceType: string;
  onSpaceType?: (id: string) => void;
}

export const RoomEditDialog: React.FC<RoomEditDialogProps> = ({ plan, rooms, step, onStep, onClose, apply, spaceType, onSpaceType }) => {
  const sole = rooms.length === 1 ? rooms[0] : null;
  const ids = rooms.map((r) => r.id);
  const key = ids.join('+');
  const steps = roomSteps(rooms.length, !!onSpaceType);
  const current = steps.includes(step) ? step : steps[0];
  const [custom, setCustom] = useState(() => !!sole && !presetOf(spaceType, sole));
  // The finish is a draft until Apply: read once from the first room.
  const [finish, setFinish] = useState<StarterFinish | null>(() => {
    const start = startingFinish();
    return start && finishOfRoom(rooms[0], start);
  });
  const first = rooms[0];
  const firstKey = first.id;
  const others = useMemo(() => (sole ? plan.rooms.filter((r) => r.id !== sole.id).map((r) => ({ id: r.id, name: r.name })) : undefined), [plan, sole]);
  // A room deleted or undone away while open.
  const live = plan.rooms.some((r) => r.id === firstKey);
  if (!live) return null;

  const resize = (size: RoomSize) => {
    if (!sole || sameSize(size, sole)) return;
    let result: PlanResult = { ok: true, plan };
    if (Math.abs(size.length - sole.length) > 1e-6 || Math.abs(size.width - sole.width) > 1e-6) {
      result = setRoomRect(plan, sole.id, { ...rectOfRoom(sole), length: size.length, width: size.width });
    }
    if (result.ok && Math.abs(size.height - sole.height) > 1e-6) result = setRoomsHeight(result.plan, [sole.id], size.height);
    apply(result, `${sole.id}:size`);
  };

  const applyFinish = () => {
    if (!finish) return;
    let result: PlanResult = { ok: true, plan };
    for (const id of ids) {
      if (!result.ok) break;
      result = applyStarterFinish(result.plan, id, finish);
    }
    if (apply(result)) onClose();
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (current === 'finish') applyFinish(); else onClose();
  };

  const StepIcon = ROOM_STEP_ICON[current];
  const title = current === 'name' ? E.nameTitle
    : current === 'space' ? W.spaceTitle
      : current === 'size' ? (sole ? (custom ? W.sizeCustomTitle : sizeTitle(spaceType)) : E.heightManyTitle)
        : current === 'ceiling' ? W.ceilingTitle : W.finishTitle;
  const intro = current === 'name' ? E.nameIntro
    : current === 'space' ? W.spaceIntro
      : current === 'size' ? (sole ? (custom ? W.sizeCustomIntro : W.sizeIntro) : E.heightManyIntro(rooms.length))
        : current === 'ceiling' ? W.ceilingIntro : E.finishIntro;

  return (
    <div
      className="re-backdrop"
      onPointerDown={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}
    >
      <form className="designer-toolbar cw-root re-dialog" role="dialog" aria-modal="true" aria-label={sole ? sole.name : S.planner.tools.many(rooms.length)} onSubmit={submit}>
        <nav className="cw-stepper re-tabs" role="tablist">
          {steps.map((s) => {
            const Icon = ROOM_STEP_ICON[s];
            return (
              <button
                key={s} type="button" role="tab" aria-selected={s === current}
                className={`cw-step${s === current ? ' is-current' : ''}`} onClick={() => onStep(s)}
              >
                <span className="cw-step-dot"><Icon size={13} /></span>
                <span className="cw-step-label">{W.steps[s]}</span>
              </button>
            );
          })}
          <span className="re-tabs-gap" />
          <button type="button" className="re-close" onClick={onClose} title={E.close} aria-label={E.close}><X size={16} /></button>
        </nav>

        <div className="cw-body" key={current}>
          <div className="cw-head">
            <span className="cw-head-icon"><StepIcon size={18} /></span>
            <div>
              <h2 className="cw-title">{title}</h2>
              <p className="cw-intro">{intro}</p>
            </div>
          </div>

          {current === 'name' && sole && (
            <input
              className="cw-name" type="text" value={sole.name} maxLength={40} aria-label={S.planner.name} placeholder={W.namePlaceholder}
              autoFocus onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => apply(renameRoom(plan, sole.id, e.target.value), `${sole.id}:name`)}
            />
          )}

          {current === 'space' && onSpaceType && <div className="cw-space"><SpacePicker value={spaceType} onChange={onSpaceType} /></div>}

          {current === 'size' && sole && (
            <SizePicker spaceType={spaceType} size={{ length: sole.length, width: sole.width, height: sole.height }} onChange={resize} custom={custom} onCustom={setCustom} />
          )}
          {current === 'size' && !sole && (
            <label className="cw-label" style={{ maxWidth: 220, marginTop: 0 }}>
              {W.wallHeight}
              <span style={{ display: 'block', marginTop: 6 }}>
                <DimensionInput
                  value={first.height} mode="ftin" min={ROOM_LIMITS.height.min} max={ROOM_LIMITS.height.max} ariaLabel={W.wallHeight}
                  onChange={(v) => { if (Math.abs(v - first.height) > 1e-6) apply(setRoomsHeight(plan, ids, v), `${key}:height`); }}
                />
              </span>
            </label>
          )}

          {current === 'ceiling' && (
            <CeilingEditor
              room={first} count={rooms.length} maxHeight={ROOM_LIMITS.height.max} variant="wizard"
              onChange={(profile) => apply(setCeilingProfile(plan, ids, profile), `${key}:ceiling`)}
              others={others}
              onCopyTo={sole ? (to) => apply(copyCeiling(plan, sole.id, to)) : undefined}
            />
          )}

          {current === 'finish' && finish && (
            <>
              <FinishStep room={first} finish={finish} onChange={setFinish} />
              <p className="re-note">{E.finishNote}</p>
            </>
          )}
        </div>

        <div className="cw-footer">
          <span className="cw-count">{sole ? sole.name : S.planner.tools.many(rooms.length)}</span>
          {current === 'finish' ? (
            <span style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="dh-btn" onClick={onClose}>{E.cancel}</button>
              <button type="submit" className="dh-btn is-accent"><Check size={15} /> {sole ? E.apply : E.applyMany(rooms.length)}</button>
            </span>
          ) : (
            <button type="submit" className="dh-btn is-accent"><Check size={15} /> {E.done}</button>
          )}
        </div>
      </form>
    </div>
  );
};

export default RoomEditDialog;
